import { FC, ReactElement, useEffect, useMemo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { Theme as MuiTheme } from "@mui/material/styles";

import { Logo } from "@/components";
import { NavItem } from "./nav-item";
import AccountPopover from "./account-popover";
import { getTDAdminOtherItems, getTdClientHomeItems, SidebarItems, getHomeItemsByAllowedKeys, getSpOwnerOtherItems } from "@/roles/sidebar-items";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";
import { roles } from "@/roles";
import { useAppSelector } from "@/store";
import { layoutDimensions, vwScale } from "@/utils/responsive";
import { closed_arrow, open_arrow } from "@/constants";
import { useGetAllClientsQuery } from "@/services";
import { tokens } from "@/tokens";

interface ISidebar {
  onClose?: () => void;
  open?: boolean;
  isSidebarExtended: boolean;
  onSidebarExtend: () => void;
}

export const Sidebar: FC<ISidebar> = ({
  open,
  onClose,
  isSidebarExtended,
  onSidebarExtend,
}): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const shouldFetchTenantPages = user?.userRole && [roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT, roles.TDCLIENT].includes(user.userRole);
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(undefined, { skip: !shouldFetchTenantPages });
  const router = useLocation();
  const mdUp = useMediaQuery((theme: MuiTheme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
    noSsr: false,
  });

  useEffect(() => {
    if (open) {
      onClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  const canSelectTenants = useMemo(() => {
    const allowedRoles = [roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);
  const isValidateRoute = window.location.pathname.includes("/validate/");

  const canSelectClients = useMemo(() => {
    // SPOWNER and SPPARTNER can always select clients
    const spRoles = [roles.SPOWNER, roles.SPPARTNER] as string[];
    const isSPRole = spRoles.includes(user?.userRole ?? "");

    // TDADMIN can also select clients if they've selected a SERVICE_PROVIDER tenant
    const isAdminWithSPTenant =
      user?.userRole === roles.TDADMIN &&
      selectedTenant?.tenantType === "SERVICE_PROVIDER";

    return isSPRole || isAdminWithSPTenant;
  }, [user, selectedTenant]);

  const clientPickerTenantId = useMemo(() => {
    const isAdmin = user?.userRole === roles.TDADMIN;
    const spRoles = [roles.SPOWNER, roles.SPPARTNER] as string[];
    const isSPRole = spRoles.includes(user?.userRole ?? "");

    // For SPOWNER and SPPARTNER, use their tenant ID from user object
    if (isSPRole && user?.tenantId) {
      return user.tenantId;
    }

    // For TDADMIN with a SERVICE_PROVIDER tenant selected, use selected tenant ID
    if (isAdmin && selectedTenant?.tenantType === "SERVICE_PROVIDER" && selectedTenant?._id) {
      return selectedTenant._id;
    }

    return "";
  }, [user?.userRole, user?.tenantId, selectedTenant]);

  useGetAllClientsQuery(
    { searchKeyword: "", pageNo: 1, pageSize: 25, tenantId: clientPickerTenantId },
    {
      skip: !canSelectClients || !clientPickerTenantId,
      refetchOnMountOrArgChange: true,
    }
  );

  const items = () => {
    return (
      <>
        <Box
          sx={{
            border: "none",
            py: 1,
          }}
        >
          {user?.userRole === roles.SPOWNER && (
            <>
              {(() => {
                const userPages = user?.allowedPages;
                const allowedKeys = userPages ?? tenantPagesResp?.pages ?? [];
                return getHomeItemsByAllowedKeys(allowedKeys);
              })().map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.SPPARTNER && (
            <>
              {(() => {
                const userPages = user?.allowedPages;
                const allowedKeys = userPages ?? tenantPagesResp?.pages ?? [];
                return getHomeItemsByAllowedKeys(allowedKeys);
              })().map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.SPCLIENT && (
            <>
              {(() => {
                const userPages = user?.allowedPages;
                const allowedKeys = userPages ?? tenantPagesResp?.pages ?? [];
                return getHomeItemsByAllowedKeys(allowedKeys);
              })().map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.TDCLIENT && (
            <>
              {getTdClientHomeItems(user, tenantPagesResp?.pages).map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.TDADMIN && (
            <>
              {SidebarItems.TDADMIN.home.map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
        </Box>
        {mdUp && (
          <Box
            sx={{
              height: "1px",
              background: tokens.border.default,
              width: isSidebarExtended ? "80%" : "100%",
              margin: "0px auto",
            }}
          />
        )}
        <Box
          sx={{
            py: 1,
          }}
        >
          {user?.userRole === roles.SPOWNER && (
            <>
              {getSpOwnerOtherItems(user, tenantPagesResp?.pages).map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.SPPARTNER && (
            <>
              {SidebarItems.SPPARTNER.others.map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.SPCLIENT && (
            <>
              {SidebarItems.SPCLIENT.others.map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.TDCLIENT && (
            <>
              {SidebarItems.TDCLIENT.others.map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
          {user?.userRole === roles.TDADMIN && (
            <>
              {getTDAdminOtherItems(user,selectedTenant).map(
                (item: ISidebarItem): ReactNode => {
                  return (
                    <NavItem
                      key={item.title}
                      icon={item.icon}
                      icon2={item.icon2}
                      href={item.href}
                      title={item.title}
                      isSidebarExtended={isSidebarExtended}
                      children={item.children}
                    />
                  );
                }
              )}
            </>
          )}
        </Box>
      </>
    );
  };

  const content = (
    <>
      <Box
        className="logged-in-sidebar-wrapper"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          backgroundColor: tokens.bg.muted,
          borderRight: "1px solid #EBEBEB",
        }}
      >
        <Box
          sx={{
            minHeight: { md: layoutDimensions.navbarDesktop, xs: layoutDimensions.navbarMobile },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px 8px",
            position: "relative",
          }}
        >
          <Logo full={isSidebarExtended} />
          {mdUp && (
            <Box
              sx={{
                position: "absolute",
                right: isSidebarExtended ? "10px" : "auto",
                left: isSidebarExtended ? "auto" : "50%",
                transform: isSidebarExtended ? "none" : "translateX(-50%)",
                top: isSidebarExtended ? "50%" : "auto",
                bottom: isSidebarExtended ? "auto" : "-12px",
                marginTop: isSidebarExtended ? "-13px" : "0",
                cursor: "pointer",
                width: vwScale(26),
                height: vwScale(26),
                zIndex: 10,
              }}
              onClick={onSidebarExtend}
            >
              <img
                src={isSidebarExtended ? closed_arrow : open_arrow}
                alt="sidebar handle"
                style={{ width: '100%', height: 'auto' }}
              />
            </Box>
          )}
        </Box>
        
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {items()}
        </Box>
        
        <Box sx={{ my: 0.25, p: 0.5 }}>
           <AccountPopover 
             isSidebarExtended={isSidebarExtended} 
             canSelectTenants={canSelectTenants}
             canSelectClients={canSelectClients}
             clientPickerTenantId={clientPickerTenantId}
             isValidateRoute={isValidateRoute}
           />
        </Box>
      </Box>
    </>
  );

  if (mdUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            color: tokens.text.primary,
            width: isSidebarExtended ? layoutDimensions.sidebarExtended : layoutDimensions.sidebarCollapsed,
            borderRight: "none",
            boxShadow: "none",
            transition: "0.3s",
            backgroundColor: tokens.bg.muted,
            display: "flex",
            zIndex: 0,
            overflowX: "hidden",
          },
        }}
        variant={"permanent"}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          color: tokens.text.primary,
          width: layoutDimensions.sidebarExtended, // Always extended on mobile
          backgroundColor: tokens.bg.muted,
          display: "flex",
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};
